import { Injectable } from '@angular/core';

type SkewCache = {
  lastCheckedAt: number;
  lastSkewMs: number;
};

type ClockSkewDebugApi = {
  state: () => {
    currentSkewMs: number;
    bannerShowing: boolean;
    cached: SkewCache | null;
    thresholdMs: number;
    checkedCleanThisSession: boolean;
  };
  clearCache: () => void;
  recordServerDate: (dateHeader: string) => void;
  recordServerTime: (epochMs: number) => void;
  simulate: (minutes: number) => void;
};

type ClockSkewWindow = Window & typeof globalThis & {
  __clockSkew?: ClockSkewDebugApi;
  __salesmaxClockSkewFetchPatched?: boolean;
  __salesmaxClockSkewRecordDate?: (dateHeader: string | null) => void;
};

@Injectable({ providedIn: 'root' })
export class ClockSkewService {
  private readonly bannerId = 'salesmax-clock-skew-alert';
  private readonly storageKey = 'salesmax-clock-skew';
  private readonly thresholdMs = 90 * 1000;
  private readonly confirmDismissMs = 3000;

  private started = false;
  private bannerShowing = false;
  private currentSkewMs = 0;
  private checkedCleanThisSession = false;
  private dismissTimer: number | null = null;
  private missingDateHeaderLogged = false;

  start(): void {
    if (this.started || typeof window === 'undefined') {
      return;
    }
    this.started = true;

    const win = window as ClockSkewWindow;
    win.__salesmaxClockSkewRecordDate = (dateHeader) => this.recordServerDate(dateHeader);
    this.installFetchObserver(win);

    const cached = this.readCache();
    if (cached && Math.abs(cached.lastSkewMs) >= this.thresholdMs) {
      this.currentSkewMs = cached.lastSkewMs;
      this.showBanner();
    }

    win.__clockSkew = {
      state: () => ({
        currentSkewMs: this.currentSkewMs,
        bannerShowing: this.bannerShowing,
        cached: this.readCache(),
        thresholdMs: this.thresholdMs,
        checkedCleanThisSession: this.checkedCleanThisSession,
      }),
      clearCache: () => {
        this.clearCache();
      },
      recordServerDate: (dateHeader: string) => {
        this.recordServerDate(dateHeader);
      },
      recordServerTime: (epochMs: number) => {
        this.recordServerTime(epochMs);
      },
      simulate: (minutes: number) => {
        this.applySkew(minutes * 60_000);
      },
    };
  }

  recordServerDate(dateHeader: string | null | undefined): void {
    if (!dateHeader) {
      return;
    }

    const serverMs = Date.parse(dateHeader);
    if (!Number.isFinite(serverMs)) {
      console.warn('[ClockSkew] could not parse Date header:', dateHeader);
      return;
    }

    this.recordServerTime(serverMs);
  }

  recordServerTime(serverEpochMs: number): void {
    if (!Number.isFinite(serverEpochMs)) {
      return;
    }

    if (this.checkedCleanThisSession && !this.bannerShowing) {
      return;
    }

    const skew = Date.now() - serverEpochMs;
    const isProblematic = Math.abs(skew) >= this.thresholdMs;

    if (isProblematic || this.bannerShowing) {
      this.applySkew(skew);
      return;
    }

    this.checkedCleanThisSession = true;
    this.currentSkewMs = skew;
    this.clearCache();
  }

  private installFetchObserver(win: ClockSkewWindow): void {
    if (win.__salesmaxClockSkewFetchPatched || typeof win.fetch !== 'function') {
      return;
    }

    const originalFetch = win.fetch.bind(win);
    win.fetch = ((input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      return originalFetch(input, init).then((response: Response) => {
        this.recordDateFromGraphqlResponse(input, response, win);
        return response;
      });
    }) as typeof win.fetch;
    win.__salesmaxClockSkewFetchPatched = true;
  }

  private recordDateFromGraphqlResponse(input: RequestInfo | URL, response: Response, win: ClockSkewWindow): void {
    if (!this.isGraphqlRequest(input, response)) {
      return;
    }

    const dateHeader = response.headers.get('date');
    if (!dateHeader && !this.missingDateHeaderLogged) {
      this.missingDateHeaderLogged = true;
      console.warn('[ClockSkew] GraphQL Date response header is not readable. Ensure CORS exposes the Date header.');
    }

    win.__salesmaxClockSkewRecordDate?.(dateHeader);
  }

  private isGraphqlRequest(input: RequestInfo | URL, response: Response): boolean {
    return this.urlLooksGraphql(this.urlFromRequest(input)) || this.urlLooksGraphql(response.url);
  }

  private urlFromRequest(input: RequestInfo | URL): string | null {
    if (typeof input === 'string') {
      return input;
    }
    if (input instanceof URL) {
      return input.href;
    }
    if (typeof Request !== 'undefined' && input instanceof Request) {
      return input.url;
    }
    return null;
  }

  private urlLooksGraphql(url: string | null): boolean {
    if (!url) {
      return false;
    }

    try {
      const parsed = new URL(url, window.location.href);
      return parsed.pathname.toLowerCase().endsWith('/graphql');
    } catch (_err) {
      return /\/graphql(?:[?#]|$)/i.test(url);
    }
  }

  private applySkew(skewMs: number): void {
    this.currentSkewMs = skewMs;

    const isProblematic = Math.abs(skewMs) >= this.thresholdMs;
    if (isProblematic) {
      this.checkedCleanThisSession = false;
      this.writeCache({ lastCheckedAt: Date.now(), lastSkewMs: skewMs });
      this.showBanner();
      return;
    }

    this.checkedCleanThisSession = true;
    this.clearCache();
    if (this.bannerShowing) {
      this.confirmAndDismissBanner();
    }
  }

  private readCache(): SkewCache | null {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as Partial<SkewCache>;
      if (typeof parsed.lastCheckedAt !== 'number' || typeof parsed.lastSkewMs !== 'number') {
        return null;
      }

      return { lastCheckedAt: parsed.lastCheckedAt, lastSkewMs: parsed.lastSkewMs };
    } catch (_err) {
      return null;
    }
  }

  private writeCache(value: SkewCache): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(value));
    } catch (_err) {
      // Storage may be unavailable in some browser modes.
    }
  }

  private clearCache(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (_err) {
      // Storage may be unavailable in some browser modes.
    }
  }

  private showBanner(): void {
    if (typeof document === 'undefined') {
      return;
    }

    if (this.dismissTimer !== null) {
      window.clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }

    const banner = document.getElementById(this.bannerId) ?? document.createElement('div');
    banner.id = this.bannerId;
    banner.setAttribute('role', 'alert');
    banner.setAttribute('aria-live', 'assertive');
    banner.style.cssText = [
      'position:fixed',
      'right:20px',
      'bottom:20px',
      'z-index:2147483646',
      'max-width:380px',
      'padding:16px',
      'border-radius:8px',
      'box-shadow:0 12px 36px rgba(0,0,0,.24)',
      'background:#1f2937',
      'color:#fff',
      'font:14px/1.45 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
    ].join(';');
    banner.innerHTML = '';

    const title = document.createElement('div');
    title.textContent = 'Your device clock is off';
    title.style.cssText = 'font-weight:600;margin-bottom:4px;color:#fbbf24';

    const body = document.createElement('div');
    body.style.cssText = 'color:#d1d5db';
    body.textContent = this.bodyText();

    banner.appendChild(title);
    banner.appendChild(body);
    if (!banner.parentElement) {
      document.body.appendChild(banner);
    }
    this.bannerShowing = true;
  }

  private confirmAndDismissBanner(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const banner = document.getElementById(this.bannerId);
    if (!banner) {
      this.bannerShowing = false;
      return;
    }

    banner.innerHTML = '';
    const ok = document.createElement('div');
    ok.textContent = 'Device clock looks correct now';
    ok.style.cssText = 'font-weight:600;color:#86efac';
    banner.appendChild(ok);

    if (this.dismissTimer !== null) {
      window.clearTimeout(this.dismissTimer);
    }
    this.dismissTimer = window.setTimeout(() => {
      banner.remove();
      this.bannerShowing = false;
      this.dismissTimer = null;
    }, this.confirmDismissMs);
  }

  private bodyText(): string {
    const minutes = Math.max(1, Math.round(Math.abs(this.currentSkewMs) / 60000));
    const direction = this.currentSkewMs > 0 ? 'ahead of' : 'behind';
    const unit = minutes === 1 ? 'minute' : 'minutes';
    return `Your device clock is about ${minutes} ${unit} ${direction} server time. This can break login, file uploads, and other actions. Turn on automatic date and time in your device settings. This message will clear after a GraphQL response confirms the clock is correct.`;
  }
}
